import React, { useState, useEffect, useMemo } from 'react';
import { fmtL } from '../../../utils/currency';
import {
    Box,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Slider,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Divider,
    useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import EastIcon from '@mui/icons-material/East';
import TuneIcon from '@mui/icons-material/Tune';
import { getStocksScreenerQuant, getStocksScreenerRatioTypes } from '../../../api/stocks_api';
import AddToWatchlistDialog from '../../IndividualStocks/Widgets/AddToWatchlistDialog';
import SearchIcon from '@mui/icons-material/Search';
import { ACTIVE_MODEL } from '../../../config/modelConfig';
const market = sessionStorage.getItem("selectedMarket") ?? "US";

/* ============================================================
   CONSTANTS
   ============================================================ */
const sectors = [
    'Technology', 'Consumer Cyclical', 'Healthcare', 'Consumer Defensive',
    'Financial Services', 'Energy', 'Communication Services', 'Industrials',
    'Basic Materials', 'Utilities', 'Real Estate',
];

// const sliderToGrade = { 1: 'F', 2: 'D', 3: 'C', 4: 'B', 5: 'A' };
// const gradeToSlider = { F: 1, D: 2, C: 3, B: 4, A: 5 };

/* Grade E added between D (2→3) and F (1). Scale now 1–6. */
const sliderToGrade = { 1: 'F', 2: 'E', 3: 'D', 4: 'C', 5: 'B', 6: 'A' };
const gradeToSlider = { F: 1, E: 2, D: 3, C: 4, B: 5, A: 6 };

const getIncludedGrades = (val) =>
    Object.entries(gradeToSlider)
        .filter(([, sv]) => sv >= val)
        .sort((a, b) => b[1] - a[1])
        .map(([g]) => g);

const gradeRank = (grade) => {
    // const map = {
    //     'A+': 1, 'A': 2, 'A-': 3,
    //     'B+': 4, 'B': 5, 'B-': 6,
    //     'C+': 7, 'C': 8, 'C-': 9,
    //     'D+': 10, 'D': 11, 'D-': 12,
    //     'F': 13,
    // };
    const map = {
        'A+': 1, 'A': 2, 'A-': 3,
        'B+': 4, 'B': 5, 'B-': 6,
        'C+': 7, 'C': 8, 'C-': 9,
        'D+': 10, 'D': 11, 'D-': 12,
        'E': 13,
        'F': 14,
    };
    return (!grade || grade === '-') ? 999 : (map[grade] ?? 999);
};

// const GRADE_SLIDER_MARKS = [
//     { value: 1, label: 'F' },
//     { value: 2, label: 'D' },
//     { value: 3, label: 'C' },
//     { value: 4, label: 'B' },
//     { value: 5, label: 'A' },
// ];

const GRADE_SLIDER_MARKS = [
    { value: 1, label: 'F' },
    { value: 2, label: 'E' },
    { value: 3, label: 'D' },
    { value: 4, label: 'C' },
    { value: 5, label: 'B' },
    { value: 6, label: 'A' },
];

const gradeSliderLabel = (val) => `Min: ${sliderToGrade[val]}`;

const staticColumns = [
    { label: 'Ticker', key: 'ticker', sortType: 'string' },
    { label: 'FA Grade', key: 'overall_grade', sortType: 'grade' },
    { label: 'Valuation', key: 'valuation_grade', sortType: 'grade' },
    { label: 'Growth', key: 'growth_grade', sortType: 'grade' },
    // { label: 'Profitability', key: 'financial_profitability_grade', sortType: 'grade' },
    {
        label: ACTIVE_MODEL === "return" ? "Profitability" : "Quality",
        key: 'financial_profitability_grade',
        sortType: 'grade'
    },
    { label: 'Risk', key: 'risk_grade', sortType: 'grade' },
    { label: 'Momentum', key: 'momentum_grade', sortType: 'grade' },
];

/* ============================================================
   HELPERS
   ============================================================ */
// const getStep = (range) => {
//     if (range > 1000) return 1;
//     if (range > 100) return 0.5;
//     if (range > 10) return 0.1;
//     return 0.01;
// };

const getStep = () => 0.01;

// const fmt = (val, step) =>
//     step < 0.1 ? Number(val).toFixed(4) : Number(val).toFixed(2);

const fmt = (val) => fmtL(val ?? 0, 2);

/* ============================================================
   SHARED SX FACTORIES
   ============================================================ */
const selectSx = (isDark) => ({
    fontSize: 'var(--font-size-sm)',
    bgcolor: isDark ? 'rgba(11,17,32,0.8)' : '#fff',
    color: 'var(--color-text-primary)',
    borderRadius: 'var(--radius-md)',
    transition: 'all var(--transition-fast)',
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isDark ? 'rgba(51,65,85,0.5)' : 'var(--color-border-primary)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-primary)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--color-primary)',
        borderWidth: '2px',
    },
    '& .MuiSvgIcon-root': { color: 'var(--color-text-muted)' },
});

const menuPaperSx = (isDark) => ({
    bgcolor: isDark ? 'rgba(11,17,32,0.98)' : '#fff',
    backdropFilter: 'blur(20px)',
    border: isDark ? '1px solid rgba(51,65,85,0.5)' : '1px solid var(--color-border-primary)',
    borderRadius: 'var(--radius-lg)',
    mt: 0.5,
    maxHeight: 260,
    boxShadow: 'var(--shadow-xl)',
});

const menuItemSx = (isDark) => ({
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    py: 1.25,
    px: 2,
    mx: 0.5,
    borderRadius: 'var(--radius-sm)',
    '&:hover': {
        bgcolor: isDark ? 'rgba(0,150,255,0.12)' : 'var(--color-primary-lighter)',
        color: 'var(--color-primary)',
    },
});

const theadCellSx = (isDark, isActive = false) => ({
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-bold)',
    color: isActive ? 'var(--color-primary)' : 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    borderBottom: isDark
        ? '1px solid rgba(51,65,85,0.5)'
        : '1px solid var(--color-border-primary)',
    bgcolor: isDark ? 'rgba(11,17,32,0.9)' : 'var(--color-bg-tertiary)',
    py: 1.75,
    px: 2,
    whiteSpace: 'nowrap',
    transition: 'color 0.15s ease',
});

const tooltipSx = (isDark) => ({
    bgcolor: isDark ? 'rgba(11,17,32,0.97)' : 'rgba(15,23,42,0.94)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(0,150,255,0.25)',
    borderRadius: 'var(--radius-lg)',
    p: 1.5,
    maxWidth: 280,
    boxShadow: 'var(--shadow-xl)',
});

const tooltipArrowSx = (isDark) => ({
    color: isDark ? 'rgba(11,17,32,0.97)' : 'rgba(15,23,42,0.94)',
});

/* ============================================================
   SLIDER SX
   ============================================================ */
const BLUE = '#0096FF';

const rangeSliderSx = (isDark) => {
    const grey = isDark ? '#2d3a4a' : '#D1D5DB';
    return {
        color: 'var(--color-primary)',
        height: 4,
        padding: '13px 0',
        '& .MuiSlider-thumb': {
            width: 14, height: 14,
            backgroundColor: BLUE,
            zIndex: 2,
            transition: 'box-shadow var(--transition-fast)',
            '&:hover': { boxShadow: '0 0 0 8px rgba(0,150,255,0.16)' },
        },
        '& .MuiSlider-track': {
            border: 'none',
            height: 4,
            backgroundColor: `${BLUE} !important`,
            opacity: 1,
        },
        '& .MuiSlider-rail': {
            opacity: 1,
            height: 4,
            backgroundColor: `${grey} !important`,
        },
        '& .MuiSlider-valueLabel': { display: 'none' },
    };
};

const gradeSliderSx = (isDark) => {
    const grey = isDark ? '#2d3a4a' : '#D1D5DB';
    return {
        color: 'var(--color-primary)',
        height: 4,
        padding: '13px 0',
        '& .MuiSlider-thumb': {
            width: 14, height: 14,
            backgroundColor: BLUE,
            zIndex: 2,
            transition: 'box-shadow var(--transition-fast)',
            '&:hover': { boxShadow: '0 0 0 8px rgba(0,150,255,0.16)' },
        },
        '& .MuiSlider-rail': {
            opacity: 1,
            height: 4,
            backgroundColor: `${BLUE} !important`,
        },
        '& .MuiSlider-track': {
            border: 'none',
            height: 4,
            backgroundColor: `${grey} !important`,
            opacity: 1,
        },
        '& .MuiSlider-mark': { display: 'none' },
        '& .MuiSlider-markLabel': {
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-tertiary)',
            fontWeight: 'var(--font-weight-semibold)',
            top: 22,
        },
    };
};

/* ============================================================
   APPLY BUTTON SX
   ============================================================ */
const applyBtnSx = (isDark, isDirty) => ({
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    textTransform: 'none',
    px: 2.5,
    py: 0.75,
    borderRadius: 'var(--radius-md)',
    minWidth: 90,
    transition: 'all var(--transition-fast)',
    ...(isDirty ? {
        bgcolor: 'var(--color-primary)',
        color: '#fff',
        boxShadow: '0 2px 8px rgba(0,150,255,0.3)',
        '&:hover': {
            bgcolor: 'var(--color-primary-dark)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 14px rgba(0,150,255,0.4)',
        },
    } : {
        bgcolor: 'transparent',
        color: 'var(--color-text-muted)',
        border: `1px solid ${isDark ? 'rgba(51,65,85,0.4)' : 'var(--color-border-primary)'}`,
        pointerEvents: 'none',
    }),
});

/* ============================================================
   SECTION LABEL — reusable uppercase micro-label
   ============================================================ */
const SectionLabel = ({ children, sx }) => (
    <Typography
        fontSize="var(--font-size-sm)"
        fontWeight="var(--font-weight-semibold)"
        letterSpacing="0.07em"
        textTransform="uppercase"
        sx={{ color: 'var(--color-text-muted)', ...sx }}
    >
        {children}
    </Typography>
);

/* ============================================================
   DRAG HINT
   ============================================================ */
const DragHint = ({ text = 'Drag the handles to set your range' }) => (
    <Box
        display="flex" alignItems="center" gap={0.5} mt={0.75}
        sx={{
            animation: 'hintFade 2.2s ease-in-out infinite',
            '@keyframes hintFade': {
                '0%,100%': { opacity: 0.3 },
                '50%': { opacity: 0.7 },
            },
        }}
    >
        <Typography fontSize="var(--font-size-sm)" sx={{ color: 'var(--color-text-muted)' }}>
            {text}
        </Typography>
        <EastIcon sx={{
            fontSize: 11,
            color: 'var(--color-text-muted)',
            animation: 'arrowNudge 1.5s ease-in-out infinite',
            '@keyframes arrowNudge': {
                '0%,100%': { transform: 'translateX(0)' },
                '50%': { transform: 'translateX(4px)' },
            },
        }} />
    </Box>
);

/* ============================================================
   GRADE TOOLTIP
   ============================================================ */
const GradeTooltipContent = ({ val }) => {
    const included = getIncludedGrades(val);
    return (
        <Box sx={{ p: 0.5 }}>
            <Typography
                fontSize="var(--font-size-sm)"
                fontWeight="var(--font-weight-bold)"
                sx={{ color: 'var(--color-text-primary)', mb: 1, opacity: 0.65 }}
            >
                Minimum {sliderToGrade[val]} — showing:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
                {included.map(g => (
                    <Box key={g} sx={{
                        px: 1, py: 0.35,
                        borderRadius: 'var(--radius-sm)',
                        bgcolor: 'rgba(0,150,255,0.45)',
                        border: '1px solid rgba(255,255,255,0.15)',
                    }}>
                        <Typography
                            fontSize="var(--font-size-sm)"
                            fontWeight="var(--font-weight-bold)"
                            sx={{ color: '#fff', lineHeight: 1.4 }}
                        >
                            {g}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

/* ============================================================
   INLINE RANGE CONTROL
   [editable min] ══●══════●══ [editable max]
   ============================================================ */
const InlineRangeControl = ({ pendingMin, pendingMax, sliderMin, sliderMax, step, onChange, isDark }) => {
    const [minStr, setMinStr] = useState(fmt(pendingMin, step));
    const [maxStr, setMaxStr] = useState(fmt(pendingMax, step));
    const [minFocused, setMinFocused] = useState(false);
    const [maxFocused, setMaxFocused] = useState(false);
    const minTimer = React.useRef(null);
    const maxTimer = React.useRef(null);

    const commitMin = (raw) => {
        const v = clamp(parseFloat(raw), sliderMin, sliderMax);
        const safe = Math.min(v, pendingMax);
        const rounded = Number(safe.toFixed(2));
        onChange([rounded, pendingMax]);
    };

    const commitMax = (raw) => {
        const v = clamp(parseFloat(raw), sliderMin, sliderMax);
        const safe = Math.max(v, pendingMin);
        const rounded = Number(safe.toFixed(2));
        onChange([pendingMin, rounded]);
    };

    useEffect(() => { if (!minFocused) setMinStr(fmt(pendingMin, step)); }, [pendingMin, step]);
    useEffect(() => { if (!maxFocused) setMaxStr(fmt(pendingMax, step)); }, [pendingMax, step]);

    const clamp = (v, lo, hi) => Math.min(Math.max(isNaN(v) ? lo : v, lo), hi);

    const handleMinCommit = () => {
        setMinFocused(false);
        const v = clamp(parseFloat(minStr), sliderMin, sliderMax);
        const safe = Math.min(v, pendingMax);
        setMinStr(fmt(safe, step));
        onChange([safe, pendingMax]);
    };

    const handleMaxCommit = () => {
        setMaxFocused(false);
        const v = clamp(parseFloat(maxStr), sliderMin, sliderMax);
        const safe = Math.max(v, pendingMin);
        setMaxStr(fmt(safe, step));
        onChange([pendingMin, safe]);
    };

    /* Shared style for the two inline editable inputs inside the pill */
    const pillInputStyle = (focused) => ({
        width: 68,
        border: 'none',
        background: 'transparent',
        color: focused ? 'var(--color-primary)' : 'var(--color-primary)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-bold)',
        textAlign: 'center',
        outline: 'revert',
        fontFamily: 'inherit',
        /* hide number spinners */
        MozAppearance: 'textfield',
        WebkitAppearance: 'none',
        padding: '0 2px',
        cursor: 'text',
    });

    return (
        <Box>
            {/* ✨ Shining Edit Hint */}
            <Box
                display="flex"
                justifyContent="flex-end"
                mb={0.5}
            >
                <Typography
                    fontSize="var(--font-size-sm)"
                    fontWeight="var(--font-weight-semibold)"
                    sx={{
                        background: `linear-gradient(
          90deg,
          var(--color-text-muted) 0%,
          var(--color-primary) 40%,
          var(--color-primary) 60%,
          var(--color-text-muted) 100%
        )`,
                        backgroundSize: '200% auto',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        animation: 'shineMove 3s linear infinite',
                        letterSpacing: '0.04em',
                        '@keyframes shineMove': {
                            '0%': { backgroundPosition: '200% center' },
                            '100%': { backgroundPosition: '-200% center' },
                        },
                    }}
                >
                    Edit the range here
                </Typography>
            </Box>

            {/* ── Range Pill Row ── */}
            <Box display="flex" justifyContent="flex-end" mb={0.75}>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 'var(--radius-md)',
                        bgcolor: isDark
                            ? 'rgba(0,150,255,0.1)'
                            : 'rgba(0,150,255,0.07)',
                        border: `1px solid ${isDark
                            ? 'rgba(0,150,255,0.3)'
                            : 'rgba(0,150,255,0.22)'}`,
                        transition: 'border-color var(--transition-fast)',
                        '&:focus-within': {
                            borderColor: 'var(--color-primary)',
                            boxShadow: '0 0 0 3px rgba(0,150,255,0.1)',
                        },
                        /* hide number spinners in webkit */
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                        },
                    }}
                >
                    <input
                        type="number"
                        value={minStr}
                        // onChange={e => setMinStr(e.target.value)}
                        onChange={(e) => {
                            const val = e.target.value;
                            setMinStr(val);

                            if (minTimer.current) clearTimeout(minTimer.current);

                            minTimer.current = setTimeout(() => {
                                commitMin(val);
                            }, 1000); // 1 second delay
                        }}
                        onFocus={() => setMinFocused(true)}
                        onBlur={handleMinCommit}
                        onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                        style={pillInputStyle(minFocused)}
                    />
                    <Typography
                        fontSize="var(--font-size-sm)"
                        fontWeight="var(--font-weight-bold)"
                        sx={{ color: 'var(--color-primary)', mx: 0.25, userSelect: 'none' }}
                    >
                        —
                    </Typography>
                    <input
                        type="number"
                        value={maxStr}
                        // onChange={e => setMaxStr(e.target.value)}
                        onChange={(e) => {
                            const val = e.target.value;
                            setMaxStr(val);

                            if (maxTimer.current) clearTimeout(maxTimer.current);

                            maxTimer.current = setTimeout(() => {
                                commitMax(val);
                            }, 1000); // 1 second delay
                        }}
                        onFocus={() => setMaxFocused(true)}
                        onBlur={handleMaxCommit}
                        onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                        style={pillInputStyle(maxFocused)}
                    />
                </Box>
            </Box>

            {/* ── Full-width slider ── */}
            <Slider
                value={[pendingMin, pendingMax]}
                // onChange={(_, v) => onChange(v)}
                onChange={(_, v) => {
                    const [lo, hi] = v;
                    onChange([
                        Number(lo.toFixed(2)),
                        Number(hi.toFixed(2))
                    ]);
                }}
                min={sliderMin}
                max={sliderMax}
                step={step}
                disableSwap
                sx={rangeSliderSx(isDark)}
            />

            {/* ── Bottom boundary labels ── */}
            <Box display="flex" justifyContent="space-between" mt={-0.5}>
                <Typography
                    fontSize="var(--font-size-sm)"
                    sx={{ color: 'var(--color-text-muted)', opacity: 0.65 }}
                >
                    {fmt(sliderMin, step)}
                </Typography>
                <Typography
                    fontSize="var(--font-size-sm)"
                    sx={{ color: 'var(--color-text-muted)', opacity: 0.65 }}
                >
                    {fmt(sliderMax, step)}
                </Typography>
            </Box>
        </Box>
    );
};

/* ============================================================
   GRADE BADGE
   ============================================================ */
const GradeBadge = ({ grade, getBg }) =>
    grade ? (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 46, py: 0.5, borderRadius: 'var(--radius-sm)',
            bgcolor: getBg(grade),
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        }}>
            <Typography variant="chipText" fontWeight="var(--font-weight-bold)" sx={{ color: '#fff', lineHeight: 1 }}>
                {grade}
            </Typography>
        </Box>
    ) : (
        <Typography variant="chipText" sx={{ color: 'var(--color-text-muted)' }}>—</Typography>
    );

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
const StocksScreenerByRatios = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [selectedSector, setSelectedSector] = useState('');
    const [pendingOverallGrade, setPendingOverallGrade] = useState(1);
    const [overallGrade, setOverallGrade] = useState(1);
    const [gradeApplied, setGradeApplied] = useState(false);
    const [ratioCategories, setRatioCategories] = useState([]);
    const [ratioFiltersState, setRatioFiltersState] = useState([]);
    const [stocks, setStocks] = useState([]);
    const [scanning, setScanning] = useState(false);
    const [totalStockCount, setTotalStockCount] = useState(0);
    const [lastSelectedCategory, setLastSelectedCategory] = useState(null);
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [watchlistDialogOpen, setWatchlistDialogOpen] = useState(false);
    const [bulkMode, setBulkMode] = useState(false);
    const [selectedTickers, setSelectedTickers] = useState([]);
    const [singleTicker, setSingleTicker] = useState(null);
    const [showAddChoice, setShowAddChoice] = useState(false);

    const getGradeBg = (grade) => {
        // const map = {
        //     'A+': theme.palette.grades.aPlus, 'A': theme.palette.grades.a, 'A-': theme.palette.grades.aMinus,
        //     'B+': theme.palette.grades.bPlus, 'B': theme.palette.grades.b, 'B-': theme.palette.grades.bMinus,
        //     'C+': theme.palette.grades.cPlus, 'C': theme.palette.grades.c, 'C-': theme.palette.grades.cMinus,
        //     'D+': theme.palette.grades.dPlus, 'D': theme.palette.grades.d, 'D-': theme.palette.grades.dMinus,
        //     'F': theme.palette.grades.f,
        // };
        const map = {
            'A+': theme.palette.grades.aPlus, 'A': theme.palette.grades.a, 'A-': theme.palette.grades.aMinus,
            'B+': theme.palette.grades.bPlus, 'B': theme.palette.grades.b, 'B-': theme.palette.grades.bMinus,
            'C+': theme.palette.grades.cPlus, 'C': theme.palette.grades.c, 'C-': theme.palette.grades.cMinus,
            'D+': theme.palette.grades.dPlus, 'D': theme.palette.grades.d, 'D-': theme.palette.grades.dMinus,
            /* Grade E — uses theme.palette.grades.e (add e:'#B71C1C' to theme.js) */
            'E': theme.palette.grades.e,
            'F': theme.palette.grades.f,
        };
        return map[grade] ?? theme.palette.grades.unknown;
    };

    useEffect(() => { fetchRatioCategories(); }, []);

    const fetchStocksWithAllFilters = async ({
        sector = selectedSector,
        grade = overallGrade,
        filters = ratioFiltersState,
        includeGrade = gradeApplied,
    } = {}) => {
        setScanning(true);
        try {
            const params = {};
            if (sector) params.sector = sector;
            if (includeGrade) params.overall_grade = sliderToGrade[grade];

            const ratiosObj = {};
            filters.forEach(f => {
                if (f.ratioKey && !!f.ratioKey) {
                    const [lo, hi] = f.range;
                    ratiosObj[f.ratioKey] = { min: lo, max: hi };
                }
            });
            if (Object.keys(ratiosObj).length > 0) params.ratios = JSON.stringify(ratiosObj);

            const res = await getStocksScreenerQuant(params);
            if (res?.success && Array.isArray(res.data?.stocks)) {
                setStocks(res.data.stocks.map(s => ({
                    ...s,
                    system_rating: s.overall_rating,
                    financial_profitability_grade: s.financial_profit_grade,
                    added: false,
                })));
                setTotalStockCount(res.data.total_stock_count ?? 0);
            } else {
                setStocks([]); setTotalStockCount(0);
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setStocks([]); setTotalStockCount(0);
        } finally {
            setScanning(false);
        }
    };

    const fetchRatioCategories = async () => {
        try {
            const res = await getStocksScreenerRatioTypes();
            if (res.success || res.response_status === 'SUCCESS') {
                setRatioCategories(res.data || res.response_data || []);
            }
        } catch (err) { console.error(err); }
    };

    const getRatioMeta = (filter) => {
        const cat = ratioCategories.find(c => c.id === filter.categoryId);
        const ratio = cat?.ratios.find(r => r.ratio_name === filter.ratioKey);
        return { sliderMin: ratio?.min_value ?? 0, sliderMax: ratio?.max_value ?? 100 };
    };

    const addFilterRow = (sameCategory = false) => {
        setRatioFiltersState(prev => [
            ...prev,
            {
                categoryId: (sameCategory && lastSelectedCategory) ? lastSelectedCategory : '', ratioKey: '', range: [0, 100],
                pendingRange: [0, 100],
                applied: false
            },
        ]);
        setShowAddChoice(false);
    };

    const updateFilterRow = (index, key, val) => {
        const updated = [...ratioFiltersState];
        updated[index] = { ...updated[index], [key]: val };

        if (key === 'categoryId') {
            updated[index].ratioKey = '';
            updated[index].range = [0, 100];
            setLastSelectedCategory(val);
        }
        if (key === 'ratioKey') {
            const cat = ratioCategories.find(c => c.id === updated[index].categoryId);
            const ratio = cat?.ratios.find(r => r.ratio_name === val);
            const mn = Number((ratio?.min_value ?? 0).toFixed(2));
            const mx = Number((ratio?.max_value ?? 100).toFixed(2));

            updated[index].range = [mn, mx];
            updated[index].pendingRange = [mn, mx];
            updated[index].applied = false;
        }
        // if (key === 'pendingRange') {
        //     updated[index].pendingRange = val;
        //     setRatioFiltersState(updated);
        //     return;
        // }
        if (key === 'pendingRange') {
            updated[index].pendingRange = val;

            // 🔥 mark as dirty immediately
            updated[index].applied = false;

            setRatioFiltersState(updated);
            return;
        }
        setRatioFiltersState(updated);
        return updated;
    };

    const removeFilterRow = (index) => {
        setRatioFiltersState(prev => {
            const next = prev.filter((_, i) => i !== index);
            if (!next.length) setLastSelectedCategory(null);
            return next;
        });
    };

    const handleApplyGrade = () => {
        setOverallGrade(pendingOverallGrade);
        setGradeApplied(true);
        fetchStocksWithAllFilters({ grade: pendingOverallGrade, includeGrade: true });
    };

    const handleApplyRatioRange = (index) => {
        const filter = ratioFiltersState[index];
        const [pMin, pMax] = filter.pendingRange ?? filter.range;
        const updated = ratioFiltersState.map((f, i) =>
            i === index ? { ...f, range: [pMin, pMax], applied: true } : f
        );
        setRatioFiltersState(updated);
        fetchStocksWithAllFilters({ filters: updated });
    };

    const handleSectorChange = (val) => {
        setSelectedSector(val);
        fetchStocksWithAllFilters({ sector: val });
    };

    const clearAllFilters = () => {
        setSelectedSector('');
        setOverallGrade(1); setPendingOverallGrade(1); setGradeApplied(false);
        setRatioFiltersState([]); setLastSelectedCategory(null);
        setStocks([]); setTotalStockCount(0);
    };

    const openSingleAdd = (t) => { setSingleTicker(t); setSelectedTickers([]); setWatchlistDialogOpen(true); };
    const openBulkAdd = () => { if (!selectedTickers.length) return; setSingleTicker(null); setWatchlistDialogOpen(true); };

    const handleDialogClose = (success) => {
        setWatchlistDialogOpen(false);
        if (success) {
            setStocks(prev => prev.map(s => {
                if (singleTicker && s.ticker === singleTicker) return { ...s, added: true };
                if (selectedTickers.includes(s.ticker)) return { ...s, added: true };
                return s;
            }));
        }
        setSingleTicker(null); setSelectedTickers([]); setBulkMode(false);
    };

    const toggleTicker = (t) =>
        setSelectedTickers(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

    const dynamicRatioKeys = useMemo(() => {
        const keys = new Set();
        stocks.forEach(s => { if (s.ratios) Object.keys(s.ratios).forEach(k => keys.add(k)); });
        return Array.from(keys);
    }, [stocks]);

    const handleSort = (col) => {
        if (sortKey === col) {
            if (sortDirection === 'asc') setSortDirection('desc');
            else { setSortKey(null); setSortDirection('asc'); }
        } else { setSortKey(col); setSortDirection('asc'); }
    };

    const sortedStocks = useMemo(() => {
        if (!sortKey) return stocks;
        const staticCol = staticColumns.find(c => c.key === sortKey);
        const isDynRatio = dynamicRatioKeys.includes(sortKey);
        if (!staticCol && !isDynRatio) return stocks;
        return [...stocks].sort((a, b) => {
            if (staticCol?.sortType === 'grade') {
                const rA = gradeRank(a[sortKey]), rB = gradeRank(b[sortKey]);
                return sortDirection === 'asc' ? rA - rB : rB - rA;
            }
            if (staticCol?.sortType === 'string') {
                const vA = (a[sortKey] || '').toLowerCase(), vB = (b[sortKey] || '').toLowerCase();
                return sortDirection === 'asc' ? vA.localeCompare(vB) : vB.localeCompare(vA);
            }
            if (isDynRatio) {
                const nA = a.ratios?.[sortKey] != null ? parseFloat(a.ratios[sortKey]) : -Infinity;
                const nB = b.ratios?.[sortKey] != null ? parseFloat(b.ratios[sortKey]) : -Infinity;
                return sortDirection === 'asc' ? nA - nB : nB - nA;
            }
            return 0;
        });
    }, [stocks, sortKey, sortDirection, dynamicRatioKeys]);

    const getSortIcon = (col) => {
        if (sortKey !== col) return <UnfoldMoreIcon sx={{ fontSize: 15, opacity: 0.35, ml: 0.5 }} />;
        return sortDirection === 'asc'
            ? <ArrowUpwardIcon sx={{ fontSize: 15, color: 'var(--color-primary)', ml: 0.5 }} />
            : <ArrowDownwardIcon sx={{ fontSize: 15, color: 'var(--color-primary)', ml: 0.5 }} />;
    };

    const hasActiveFilters = selectedSector || overallGrade > 1 ||
        ratioFiltersState.some(f => !!f.ratioKey);

    const activeFilterCount = [
        selectedSector ? 1 : 0,
        overallGrade > 1 ? 1 : 0,
        ratioFiltersState.filter(f => !!f.ratioKey).length,
    ].reduce((a, b) => a + b, 0);

    const formatRatioName = (n) =>
        n.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    /* ── card wrapper shared sx ── */
    const cardSx = {
        bgcolor: isDark ? 'rgba(17,24,39,0.7)' : '#fff',
        backdropFilter: 'blur(24px)',
        border: isDark
            ? '1px solid rgba(51,65,85,0.45)'
            : '1px solid var(--color-border-primary)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: isDark ? 'var(--shadow-lg)' : 'var(--shadow-sm)',
        transition: 'all var(--transition-normal)',
    };

    /* ============================================================
       RENDER
       ============================================================ */
    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: isDark ? '#080d18' : 'var(--color-bg-primary)',
            backgroundImage: isDark
                ? 'radial-gradient(ellipse at 15% 0%, rgba(0,150,255,0.06) 0%, transparent 55%), radial-gradient(ellipse at 85% 100%, rgba(59,130,246,0.04) 0%, transparent 50%)'
                : 'radial-gradient(ellipse at 15% 0%, rgba(0,150,255,0.07) 0%, transparent 55%)',
            px: { xs: 2, md: 4 },
            py: 3.5,
            transition: 'background-color var(--transition-normal)',
        }}>

            {/* ══════════════════════════════════════
                HEADER
            ══════════════════════════════════════ */}
            <Box mb={3.5}>
                <Typography
                    variant="title"
                    fontWeight="var(--font-weight-bold)"
                    sx={{ color: 'var(--color-text-primary)', display: 'block' }}
                    letterSpacing="-0.02em"
                    mb={0.5}
                >
                    Stock Screener — Ratio Based Analysis
                </Typography>
                <Typography variant="subtitle" fontWeight="var(--font-weight-normal)" sx={{ color: 'var(--color-text-muted)' }}>
                    Multi-factor S&amp;P 500 screening with customizable fundamental ratio filters
                </Typography>
            </Box>

            {market === "ALL" ? (<>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        p: 3,
                        borderRadius: "var(--radius-lg)",
                        border: "1px solid var(--color-info)",
                        bgcolor: "var(--color-info-light)",
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "var(--radius-md)",
                            bgcolor: "var(--color-info)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <Typography sx={{ color: "#fff", fontWeight: "var(--font-weight-black)", fontSize: "var(--font-size-lg)" }}>
                            !
                        </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column">
                        <Typography variant="tableHeader" fontWeight="var(--font-weight-bold)" sx={{ color: "var(--color-text-primary)" }} mb={0.5}>
                            Market Selection Required
                        </Typography>
                        <Typography variant="paragraph" sx={{ color: "var(--color-text-secondary)" }}>
                            The Stock Screener is not available in <strong>All Markets</strong> mode. Please select a specific market (e.g. <strong>US</strong> or <strong>TR</strong>) from the market selector to access the screener.
                        </Typography>
                    </Box>
                </Box>
            </>) : (<>
                {/* ══════════════════════════════════════
                FILTER CARD
            ══════════════════════════════════════ */}
                <Box sx={{ ...cardSx, p: 0, mb: 2, overflow: 'hidden' }}>

                    {/* Card top-bar */}
                    <Box
                        display="flex" justifyContent="space-between" alignItems="center"
                        sx={{
                            px: 3.5, py: 2.25,
                            bgcolor: isDark ? 'rgba(11,17,32,0.6)' : 'var(--color-bg-tertiary)',
                            borderBottom: isDark
                                ? '1px solid rgba(51,65,85,0.4)'
                                : '1px solid var(--color-border-primary)',
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1.25}>
                            <TuneIcon sx={{ fontSize: 18, color: 'var(--color-primary)' }} />
                            <Typography
                                variant="tableHeader"
                                fontWeight="var(--font-weight-bold)"
                                sx={{ color: 'var(--color-text-primary)' }}
                            >
                                Filter Criteria
                            </Typography>
                            {hasActiveFilters && (
                                <Chip
                                    label={`${activeFilterCount} active`}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        bgcolor: 'rgba(0,150,255,0.14)',
                                        color: 'var(--color-primary)',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 'var(--font-weight-bold)',
                                        border: '1px solid rgba(0,150,255,0.3)',
                                    }}
                                />
                            )}
                        </Box>
                        <Button
                            startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                            onClick={clearAllFilters}
                            disabled={!hasActiveFilters}
                            size="small"
                            sx={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-semibold)',
                                color: 'var(--color-text-muted)',
                                opacity: hasActiveFilters ? 1 : 0.4,
                                textTransform: 'none',
                                px: 1.5,
                                borderRadius: 'var(--radius-md)',
                                '&:hover': {
                                    bgcolor: 'rgba(239,68,68,0.08)',
                                    color: 'var(--color-error)',
                                },
                            }}
                        >
                            Clear all
                        </Button>
                    </Box>

                    {/* Card body */}
                    <Box sx={{ px: 3.5, py: 3 }}>

                        {/* ── Row 1: Sector + Grade ── */}
                        <Box display="grid" gridTemplateColumns="1fr 2fr" gap={4} mb={4}>

                            {/* Sector */}
                            <Box>
                                <SectionLabel sx={{ mb: 1.25 }}>Sector</SectionLabel>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={selectedSector}
                                        displayEmpty
                                        onChange={(e) => handleSectorChange(e.target.value)}
                                        sx={selectSx(isDark)}
                                        MenuProps={{ PaperProps: { sx: menuPaperSx(isDark) } }}
                                    >
                                        <MenuItem value="">
                                            <Typography fontSize="var(--font-size-sm)" sx={{ color: 'var(--color-text-muted)' }}>
                                                All Sectors
                                            </Typography>
                                        </MenuItem>
                                        {sectors.map(s => (
                                            <MenuItem key={s} value={s} sx={menuItemSx(isDark)}>{s}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Overall Grade */}
                            <Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.25}>
                                    <SectionLabel>Overall FA Grade</SectionLabel>
                                    <Box sx={{
                                        px: 1.25, py: 0.25,
                                        borderRadius: 'var(--radius-sm)',
                                        bgcolor: pendingOverallGrade > 1
                                            ? 'rgba(0,150,255,0.12)'
                                            : isDark ? 'rgba(51,65,85,0.3)' : 'var(--color-bg-tertiary)',
                                        border: pendingOverallGrade > 1
                                            ? '1px solid rgba(0,150,255,0.3)'
                                            : `1px solid ${isDark ? 'rgba(51,65,85,0.4)' : 'var(--color-border-primary)'}`,
                                    }}>
                                        <Typography
                                            fontSize="var(--font-size-sm)"
                                            fontWeight="var(--font-weight-bold)"
                                            sx={{ color: pendingOverallGrade > 1 ? 'var(--color-primary)' : 'var(--color-text-muted)' }}
                                        >
                                            {gradeSliderLabel(pendingOverallGrade)}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Tooltip
                                    title={<GradeTooltipContent val={pendingOverallGrade} />}
                                    placement="top" arrow
                                    componentsProps={{
                                        tooltip: { sx: tooltipSx(isDark) },
                                        arrow: { sx: tooltipArrowSx(isDark) },
                                    }}
                                >
                                    <Box sx={{ px: 0.5 }}>
                                        <Slider
                                            value={pendingOverallGrade}
                                            onChange={(_, v) => setPendingOverallGrade(v)}
                                            // min={1} max={5} step={1}
                                            min={1} max={6} step={1}
                                            marks={GRADE_SLIDER_MARKS}
                                            sx={gradeSliderSx(isDark)}
                                        />
                                    </Box>
                                </Tooltip>

                                {pendingOverallGrade === 1 && (
                                    <DragHint text="Drag right to select minimum FA grade" />
                                )}

                                <Box display="flex" justifyContent="flex-end" mt={2}>
                                    <Button
                                        size="small"
                                        onClick={handleApplyGrade}
                                        startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                                        disabled={gradeApplied && pendingOverallGrade === overallGrade}
                                        sx={applyBtnSx(isDark, !gradeApplied || pendingOverallGrade !== overallGrade)}
                                    >
                                        {gradeApplied && pendingOverallGrade === overallGrade ? 'Applied' : 'Apply'}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                        {/* ── Divider ── */}
                        <Divider sx={{
                            mb: 3,
                            borderColor: isDark ? 'rgba(51,65,85,0.4)' : 'var(--color-border-primary)',
                        }} />

                        {/* ── Ratio Filters heading ── */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                            <SectionLabel fontSize="var(--font-size-sm)">Fundamental Ratio Filters</SectionLabel>
                            {ratioFiltersState.length > 0 && (
                                <Typography fontSize="var(--font-size-sm)" sx={{ color: 'var(--color-text-muted)' }}>
                                    {ratioFiltersState.filter(f => !!f.ratioKey).length} of {ratioFiltersState.length} configured
                                </Typography>
                            )}
                        </Box>

                        {/* ── Filter rows ── */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {ratioFiltersState.map((filter, index) => {
                                const selectedCategory = ratioCategories.find(c => c.id === filter.categoryId);
                                const showCatSelect = index === 0 || ratioFiltersState[index - 1]?.categoryId !== filter.categoryId;
                                const { sliderMin, sliderMax } = getRatioMeta(filter);
                                const step = getStep(Math.abs(sliderMax - sliderMin));

                                const [pendingMin, pendingMax] = filter.pendingRange ?? [sliderMin, sliderMax];
                                const [appliedMin, appliedMax] = filter.range ?? [sliderMin, sliderMax];
                                const isRangeDirty = pendingMin !== appliedMin || pendingMax !== appliedMax;
                                const isAtFullRange = pendingMin === sliderMin && pendingMax === sliderMax;

                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            bgcolor: isDark
                                                ? 'rgba(11,17,32,0.5)'
                                                : 'var(--color-bg-secondary)',
                                            border: isDark
                                                ? '1px solid rgba(51,65,85,0.4)'
                                                : '1px solid var(--color-border-primary)',
                                            borderRadius: 'var(--radius-lg)',
                                            overflow: 'hidden',
                                            transition: 'border-color var(--transition-fast)',
                                            '&:hover': {
                                                borderColor: isDark
                                                    ? 'rgba(0,150,255,0.25)'
                                                    : 'rgba(0,150,255,0.3)',
                                            },
                                        }}
                                    >
                                        {/* Row header — index badge + delete */}
                                        <Box
                                            display="flex" alignItems="center" justifyContent="space-between"
                                            sx={{
                                                px: 2.5, py: 1.25,
                                                bgcolor: isDark
                                                    ? 'rgba(0,0,0,0.2)'
                                                    : 'rgba(0,150,255,0.03)',
                                                borderBottom: isDark
                                                    ? '1px solid rgba(51,65,85,0.3)'
                                                    : '1px solid var(--color-border-primary)',
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Box sx={{
                                                    width: 20, height: 20,
                                                    borderRadius: '50%',
                                                    bgcolor: 'rgba(0,150,255,0.15)',
                                                    border: '1px solid rgba(0,150,255,0.3)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <Typography
                                                        fontSize="var(--font-size-sm)"
                                                        fontWeight="var(--font-weight-bold)"
                                                        sx={{ color: 'var(--color-primary)', lineHeight: 1 }}
                                                    >
                                                        {index + 1}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    fontSize="var(--font-size-sm)"
                                                    fontWeight="var(--font-weight-semibold)"
                                                    sx={{ color: 'var(--color-text-muted)' }}
                                                >
                                                    {filter.ratioKey
                                                        ? formatRatioName(filter.ratioKey)
                                                        : 'Configure this filter'}
                                                </Typography>
                                                {filter.applied && !isRangeDirty && (
                                                    <Chip
                                                        label="Applied"
                                                        size="small"
                                                        icon={<CheckIcon sx={{ fontSize: '11px !important', color: 'var(--color-success) !important' }} />}
                                                        sx={{
                                                            height: 18,
                                                            bgcolor: 'rgba(16,185,129,0.1)',
                                                            color: 'var(--color-success)',
                                                            fontSize: 'var(--font-size-sm)',
                                                            fontWeight: 'var(--font-weight-semibold)',
                                                            border: '1px solid rgba(16,185,129,0.25)',
                                                            '& .MuiChip-label': { px: 0.75 },
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Tooltip title="Remove filter" placement="left">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeFilterRow(index)}
                                                    sx={{
                                                        color: 'var(--color-text-muted)',
                                                        '&:hover': {
                                                            color: 'var(--color-error)',
                                                            bgcolor: 'rgba(239,68,68,0.08)',
                                                        },
                                                    }}
                                                >
                                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        {/* Row body */}
                                        <Box sx={{ px: 2.5, pt: 2, pb: 2.5 }}>

                                            {/* Selects row */}
                                            <Box display="flex" gap={2} alignItems="flex-end" mb={filter.ratioKey ? 3 : 0}>
                                                {/* Category */}
                                                {showCatSelect ? (
                                                    <Box sx={{ minWidth: 190 }}>
                                                        <SectionLabel sx={{ mb: 1 }}>Category</SectionLabel>
                                                        <FormControl fullWidth size="small">
                                                            <Select
                                                                value={filter.categoryId}
                                                                displayEmpty
                                                                onChange={(e) => updateFilterRow(index, 'categoryId', e.target.value)}
                                                                sx={selectSx(isDark)}
                                                                MenuProps={{ PaperProps: { sx: menuPaperSx(isDark) } }}
                                                            >
                                                                <MenuItem value="">
                                                                    <Typography fontSize="var(--font-size-sm)" sx={{ color: 'var(--color-text-muted)' }}>
                                                                        Select category
                                                                    </Typography>
                                                                </MenuItem>
                                                                {ratioCategories.map(cat => (
                                                                    <MenuItem key={cat.id} value={cat.id} sx={menuItemSx(isDark)}>
                                                                        {cat.category_name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                ) : (
                                                    selectedCategory && (
                                                        <Box sx={{ minWidth: 190 }}>
                                                            <SectionLabel sx={{ mb: 1 }}>Category</SectionLabel>
                                                            <Chip
                                                                label={selectedCategory.category_name}
                                                                size="small"
                                                                sx={{
                                                                    height: 36,
                                                                    px: 0.5,
                                                                    bgcolor: 'rgba(16,185,129,0.1)',
                                                                    color: 'var(--color-success)',
                                                                    fontSize: 'var(--font-size-sm)',
                                                                    fontWeight: 'var(--font-weight-semibold)',
                                                                    border: '1px solid rgba(16,185,129,0.25)',
                                                                    borderRadius: 'var(--radius-md)',
                                                                }}
                                                            />
                                                        </Box>
                                                    )
                                                )}

                                                {/* Ratio */}
                                                <Box sx={{ flex: 1 }}>
                                                    <SectionLabel sx={{ mb: 1 }} fontSize="var(--font-size-sm)">Ratio Type</SectionLabel>
                                                    <FormControl fullWidth size="small">
                                                        <Select
                                                            value={filter.ratioKey}
                                                            displayEmpty
                                                            disabled={!filter.categoryId}
                                                            onChange={(e) => updateFilterRow(index, 'ratioKey', e.target.value)}
                                                            sx={selectSx(isDark)}
                                                            MenuProps={{ PaperProps: { sx: menuPaperSx(isDark) } }}
                                                        >
                                                            <MenuItem value="">
                                                                <Typography fontSize="var(--font-size-sm)" sx={{ color: 'var(--color-text-muted)' }}>
                                                                    {filter.categoryId ? 'Select ratio' : 'Select a category first'}
                                                                </Typography>
                                                            </MenuItem>
                                                            {selectedCategory?.ratios.map(r => (
                                                                <MenuItem key={r.id} value={r.ratio_name} sx={menuItemSx(isDark)}>
                                                                    {formatRatioName(r.ratio_name)}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            </Box>

                                            {/* Range control */}
                                            {filter.ratioKey && (
                                                <Box
                                                    sx={{
                                                        bgcolor: isDark
                                                            ? 'rgba(0,0,0,0.2)'
                                                            : 'rgba(0,150,255,0.02)',
                                                        border: isDark
                                                            ? '1px solid rgba(51,65,85,0.35)'
                                                            : '1px solid rgba(0,150,255,0.12)',
                                                        borderRadius: 'var(--radius-md)',
                                                        px: 2.5,
                                                        pt: 2,
                                                        pb: 1.5,
                                                    }}
                                                >
                                                    <InlineRangeControl
                                                        pendingMin={pendingMin}
                                                        pendingMax={pendingMax}
                                                        sliderMin={sliderMin}
                                                        sliderMax={sliderMax}
                                                        step={step}
                                                        isDark={isDark}
                                                        onChange={(v) => updateFilterRow(index, 'pendingRange', v)}
                                                    />
                                                    {isAtFullRange && <DragHint />}
                                                    <Box display="flex" justifyContent="flex-end" mt={1.5}>
                                                        <Button
                                                            size="small"
                                                            disabled={filter.applied && !isRangeDirty}
                                                            startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                                                            onClick={() => handleApplyRatioRange(index)}
                                                            sx={applyBtnSx(isDark, !filter.applied || isRangeDirty)}
                                                        >
                                                            {filter.applied && !isRangeDirty ? 'Applied' : 'Apply'}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* ── Add filter button ── */}
                        <Box mt={ratioFiltersState.length ? 2.5 : 0}>
                            <Button
                                startIcon={<AddIcon sx={{ fontSize: 17 }} />}
                                onClick={() => {
                                    if (!ratioFiltersState.length || !lastSelectedCategory) addFilterRow(false);
                                    else setShowAddChoice(true);
                                }}
                                sx={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    textTransform: 'none',
                                    color: 'var(--color-primary)',
                                    bgcolor: 'transparent',
                                    border: '1.5px dashed rgba(0,150,255,0.35)',
                                    borderRadius: 'var(--radius-lg)',
                                    px: 3, py: 1.25,
                                    width: '100%',
                                    transition: 'all var(--transition-fast)',
                                    '&:hover': {
                                        bgcolor: 'rgba(0,150,255,0.06)',
                                        borderColor: 'var(--color-primary)',
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                Add Ratio Filter
                            </Button>
                        </Box>

                        {/* Same / Different category choice */}
                        {showAddChoice && (
                            <Box sx={{
                                mt: 2,
                                bgcolor: isDark ? 'rgba(11,17,32,0.6)' : 'var(--color-bg-tertiary)',
                                border: `1px solid ${isDark ? 'rgba(51,65,85,0.4)' : 'var(--color-border-primary)'}`,
                                borderRadius: 'var(--radius-lg)',
                                p: 2.5,
                            }}>
                                <Typography
                                    fontSize="var(--font-size-sm)"
                                    fontWeight="var(--font-weight-semibold)"
                                    sx={{ color: 'var(--color-text-primary)' }}
                                    mb={2}
                                >
                                    Add ratio from:
                                </Typography>
                                <Box display="flex" gap={1.5} alignItems="center">
                                    <Button
                                        onClick={() => addFilterRow(true)}
                                        sx={{
                                            flex: 1,
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 'var(--font-weight-semibold)',
                                            textTransform: 'none',
                                            color: 'var(--color-success)',
                                            bgcolor: 'rgba(16,185,129,0.08)',
                                            border: '1px solid rgba(16,185,129,0.25)',
                                            borderRadius: 'var(--radius-lg)',
                                            py: 1.25,
                                            '&:hover': { bgcolor: 'rgba(16,185,129,0.15)', borderColor: 'var(--color-success)' },
                                        }}
                                    >
                                        Same Category&nbsp;
                                        <Typography component="span" fontSize="var(--font-size-sm)" sx={{ opacity: 0.7 }}>
                                            ({ratioCategories.find(c => c.id === lastSelectedCategory)?.category_name})
                                        </Typography>
                                    </Button>
                                    <Button
                                        onClick={() => addFilterRow(false)}
                                        sx={{
                                            flex: 1,
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 'var(--font-weight-semibold)',
                                            textTransform: 'none',
                                            color: 'var(--color-warning)',
                                            bgcolor: 'rgba(245,158,11,0.08)',
                                            border: '1px solid rgba(245,158,11,0.25)',
                                            borderRadius: 'var(--radius-lg)',
                                            py: 1.25,
                                            '&:hover': { bgcolor: 'rgba(245,158,11,0.15)', borderColor: 'var(--color-warning)' },
                                        }}
                                    >
                                        Different Category
                                    </Button>
                                    <IconButton
                                        onClick={() => setShowAddChoice(false)}
                                        size="small"
                                        sx={{
                                            color: 'var(--color-text-muted)',
                                            '&:hover': { color: 'var(--color-error)', bgcolor: 'rgba(239,68,68,0.08)' },
                                        }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════
                BULK TOGGLE
            ══════════════════════════════════════ */}
                {stocks.length > 0 && (
                    <Box display="flex" justifyContent="flex-end" mb={1.5}>
                        <Button
                            onClick={() => { setBulkMode(p => !p); setSelectedTickers([]); }}
                            size="small"
                            sx={{
                                bgcolor: bulkMode ? 'rgba(239,68,68,0.08)' : 'rgba(59,130,246,0.08)',
                                color: bulkMode ? 'var(--color-error)' : 'var(--color-info)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-semibold)',
                                textTransform: 'none',
                                px: 2.5, py: 1,
                                borderRadius: 'var(--radius-lg)',
                                border: bulkMode
                                    ? '1px solid rgba(239,68,68,0.25)'
                                    : '1px solid rgba(59,130,246,0.25)',
                                transition: 'all var(--transition-fast)',
                                '&:hover': {
                                    bgcolor: bulkMode ? 'rgba(239,68,68,0.14)' : 'rgba(59,130,246,0.14)',
                                    borderColor: bulkMode ? 'var(--color-error)' : 'var(--color-info)',
                                },
                            }}
                        >
                            {bulkMode ? 'Cancel Selection' : '+ Bulk Add to Watchlist'}
                        </Button>
                    </Box>
                )}

                {/* ══════════════════════════════════════
                RESULTS TABLE
            ══════════════════════════════════════ */}
                <Box sx={{
                    ...cardSx,
                    overflow: 'hidden',
                    mb: 2.5,
                    maxHeight: 540,
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    {/* Bulk selection bar */}
                    {bulkMode && selectedTickers.length > 0 && (
                        <Box sx={{
                            px: 3, py: 2,
                            bgcolor: isDark ? 'rgba(11,17,32,0.8)' : 'var(--color-bg-tertiary)',
                            borderBottom: isDark
                                ? '1px solid rgba(51,65,85,0.4)'
                                : '1px solid var(--color-border-primary)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <Typography variant="tableHeader" fontWeight="var(--font-weight-semibold)" sx={{ color: 'var(--color-text-primary)' }}>
                                {selectedTickers.length} stock{selectedTickers.length !== 1 ? 's' : ''} selected
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={openBulkAdd}
                                size="small"
                                sx={{
                                    textTransform: 'none',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    px: 2.5, py: 0.875,
                                    bgcolor: 'var(--color-primary)',
                                    borderRadius: 'var(--radius-md)',
                                    boxShadow: '0 2px 8px rgba(0,150,255,0.3)',
                                    '&:hover': { bgcolor: 'var(--color-primary-dark)' },
                                }}
                            >
                                Add Selected to Watchlist
                            </Button>
                        </Box>
                    )}

                    <Box sx={{ overflowY: 'auto', flex: 1 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {bulkMode && (
                                        <TableCell sx={{ ...theadCellSx(isDark), width: 48 }}>
                                            <input
                                                type="checkbox"
                                                checked={stocks.length > 0 && selectedTickers.length === stocks.length}
                                                onChange={(e) => setSelectedTickers(e.target.checked ? stocks.map(s => s.ticker) : [])}
                                                style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                                            />
                                        </TableCell>
                                    )}
                                    {staticColumns.map(col => (
                                        <TableCell
                                            key={col.key}
                                            onClick={() => handleSort(col.key)}
                                            sx={{
                                                ...theadCellSx(isDark, sortKey === col.key),
                                                cursor: 'pointer', userSelect: 'none',
                                                '&:hover': { color: 'var(--color-primary)' },
                                            }}
                                        >
                                            <Box display="flex" alignItems="center">
                                                {col.label}{getSortIcon(col.key)}
                                            </Box>
                                        </TableCell>
                                    ))}
                                    {dynamicRatioKeys.map(key => (
                                        <TableCell
                                            key={key}
                                            onClick={() => handleSort(key)}
                                            sx={{
                                                ...theadCellSx(isDark, sortKey === key),
                                                cursor: 'pointer', userSelect: 'none',
                                                '&:hover': { color: 'var(--color-primary)' },
                                            }}
                                        >
                                            <Box display="flex" alignItems="center">
                                                {formatRatioName(key)}{getSortIcon(key)}
                                            </Box>
                                        </TableCell>
                                    ))}
                                    {!bulkMode && (
                                        <TableCell sx={theadCellSx(isDark)}>Action</TableCell>
                                    )}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {scanning ? (
                                    <TableRow>
                                        <TableCell colSpan={14} sx={{ py: 10, textAlign: 'center', border: 'none' }}>
                                            <CircularProgress size={36} thickness={4} sx={{ color: 'var(--color-primary)' }} />
                                            <Typography variant="tableHeader" sx={{ color: 'var(--color-text-muted)' }} mt={2} display="block">
                                                Scanning stocks…
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : stocks.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={14} sx={{ py: 10, textAlign: 'center', border: 'none' }}>
                                            <Typography variant="tableHeader" sx={{ color: 'var(--color-text-muted)' }}>
                                                {hasActiveFilters
                                                    ? 'No stocks match the current filter criteria'
                                                    : 'Apply filters above to scan for stocks'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedStocks.map((stock, idx) => (
                                        <TableRow
                                            key={stock.ticker}
                                            sx={{
                                                transition: 'background var(--transition-fast)',
                                                '&:hover': {
                                                    bgcolor: isDark
                                                        ? 'rgba(0,150,255,0.04)'
                                                        : 'var(--color-primary-lighter)',
                                                },
                                                borderBottom: idx === sortedStocks.length - 1
                                                    ? 'none'
                                                    : isDark
                                                        ? '1px solid rgba(51,65,85,0.3)'
                                                        : '1px solid var(--color-border-primary)',
                                            }}
                                        >
                                            {bulkMode && (
                                                <TableCell sx={{ py: 1.75, px: 2 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTickers.includes(stock.ticker)}
                                                        onChange={() => toggleTicker(stock.ticker)}
                                                        style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell sx={{ py: 1.75, px: 2 }}>
                                                <Typography
                                                    variant="chipText"
                                                    fontWeight="var(--font-weight-bold)"
                                                    sx={{ color: 'var(--color-primary)' }}
                                                    letterSpacing="0.01em"
                                                >
                                                    {stock.ticker}
                                                </Typography>
                                            </TableCell>
                                            {[
                                                stock.overall_grade,
                                                stock.valuation_grade,
                                                stock.growth_grade,
                                                stock.financial_profitability_grade,
                                                stock.risk_grade,
                                                stock.momentum_grade,
                                            ].map((g, i) => (
                                                <TableCell key={i} sx={{ py: 1.75, px: 2 }}>
                                                    <GradeBadge grade={g} getBg={getGradeBg} />
                                                </TableCell>
                                            ))}
                                            {dynamicRatioKeys.map(key => (
                                                <TableCell key={key} sx={{ py: 1.75, px: 2 }}>
                                                    <Typography variant="tableHeader" sx={{ color: 'var(--color-text-primary)' }}>
                                                        {stock.ratios?.[key] != null ? Number(stock.ratios[key]).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ py: 1.75, px: 2 }}>
                                                {stock.added ? (
                                                    <Chip
                                                        icon={<CheckIcon sx={{ fontSize: '13px !important' }} />}
                                                        label="Added"
                                                        size="small"
                                                        sx={{
                                                            height: 26,
                                                            bgcolor: 'rgba(16,185,129,0.1)',
                                                            color: 'var(--color-success)',
                                                            fontSize: 'var(--font-size-sm)',
                                                            fontWeight: 'var(--font-weight-bold)',
                                                            border: '1px solid rgba(16,185,129,0.25)',
                                                        }}
                                                    />
                                                ) : bulkMode ? null : (
                                                    <Button
                                                        startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                                                        onClick={() => openSingleAdd(stock.ticker)}
                                                        size="small"
                                                        sx={{
                                                            fontSize: 'var(--font-size-sm)',
                                                            fontWeight: 'var(--font-weight-semibold)',
                                                            textTransform: 'none',
                                                            color: 'var(--color-primary)',
                                                            px: 1.5,
                                                            borderRadius: 'var(--radius-md)',
                                                            border: '1px solid rgba(0,150,255,0.25)',
                                                            transition: 'all var(--transition-fast)',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(0,150,255,0.08)',
                                                                borderColor: 'var(--color-primary)',
                                                            },
                                                        }}
                                                    >
                                                        Watchlist
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>

                {/* ══════════════════════════════════════
                ANALYTICS CARD
            ══════════════════════════════════════ */}
                <Box sx={{ ...cardSx, p: 4 }}>
                    <Typography
                        variant="subtitle"
                        fontWeight="var(--font-weight-semibold)"
                        sx={{ color: 'var(--color-text-primary)', display: 'block' }}
                        mb={0.75} letterSpacing="-0.01em"
                    >
                        Index Correlation Analytics
                    </Typography>
                    <Typography
                        variant="paragraph"
                        sx={{ color: 'var(--color-text-muted)', display: 'block', maxWidth: 820 }}
                        mb={4} lineHeight={1.75}
                    >
                        All quantitative ratings are synthesized using advanced reasoning over real-time financial
                        fundamentals and momentum trends.
                    </Typography>

                    <Box display="flex" gap={6}>
                        {[
                            {
                                label: 'Stocks Screened',
                                value: stocks.length,
                                gradient: isDark
                                    ? 'linear-gradient(135deg,#e2e8f0 0%,#94a3b8 100%)'
                                    : 'linear-gradient(135deg,#1e293b 0%,#475569 100%)',
                            },
                            {
                                label: 'Total Index Stocks',
                                value: totalStockCount,
                                gradient: 'linear-gradient(135deg,#3b82f6 0%,#1d4ed8 100%)',
                            },
                        ].map(({ label, value, gradient }) => (
                            <Box key={label}>
                                <SectionLabel sx={{ mb: 1.5, display: 'block' }}>{label}</SectionLabel>
                                <Typography sx={{
                                    fontSize: 'var(--font-size-5xl)',
                                    fontWeight: 'var(--font-weight-black)',
                                    background: gradient,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    lineHeight: 1,
                                }}>
                                    {value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

            </>)}

            <AddToWatchlistDialog
                open={watchlistDialogOpen}
                ticker={singleTicker}
                tickers={selectedTickers}
                onClose={handleDialogClose}
            />
        </Box>
    );
};

export default StocksScreenerByRatios;